// RunwayDiagram — top-down SVG depiction of an airport's runways.
//
// Wiley field feedback 2026-05-13 15:10 PT: "When you click on an
// airport, looking at the runway, there should be a graphical
// depiction of the runway and taxiways or perhaps even an actual
// photo or both."
//
// Sources:
//   - NASR runway end coords (lat/lon) for each runway end
//   - NASR runway length + width for proportional rendering
//   - Airport center lat/lon as the diagram origin
//
// Conventions:
//   - North-up by default (true north). Toggle to magnetic-north on
//     tap so it matches the cockpit instrument convention.
//   - Each runway drawn to scale relative to other runways at the
//     same airport (proportional length + width preserved).
//   - Designator labels at each end, sized to read at a glance.
//   - Closed runways shown gray + X-hatched (matches sectional
//     convention).
//
// Taxiways are NOT in NASR. Future enhancement: pull from OSM
// aeroway=taxiway features and render the same way.

import { useMemo, useState } from 'react';
import type { NasrAirportDetail } from '../lib/airportNasr';

interface Props {
  /** Loaded NASR airport detail — must have at least the airport
   *  center + at least one runway with both end coords. Null while
   *  loading or for non-US airports without NASR coverage. */
  nasr: NasrAirportDetail | null;
}

/** Internal: one runway projected to screen coords. */
interface ProjectedRunway {
  ident: string;
  /** Both end designators, e.g. ['16', '34']. */
  leIdent: string;
  heIdent: string;
  /** Pixel coords of each end (centerline endpoints). */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Length / width in feet (for the stroke-width calc + tooltip). */
  lengthFt: number;
  widthFt: number;
  /** Closed runways painted differently. */
  closed: boolean;
}

const PADDING_PX = 28;          // breathing room around the runway extents
const MIN_RUNWAY_STROKE = 4;    // px — never thinner than this so it reads
const MAX_RUNWAY_STROKE = 14;   // px — never wider than this so it doesn't overwhelm

export default function RunwayDiagram({ nasr }: Props) {
  // Magnetic-north orientation toggle. Default OFF (north-up = true
  // north, the chart convention). Magnetic-up matches the HSI / DG
  // convention the pilot reads in the cockpit; toggle on demand.
  const [magNorthUp, setMagNorthUp] = useState(false);

  // Project every runway to a unit-coord box; the SVG viewBox is
  // computed to contain all ends with PADDING_PX margin.
  const projection = useMemo(() => {
    if (!nasr) return null;
    return projectRunways(nasr, magNorthUp);
  }, [nasr, magNorthUp]);

  if (!nasr) {
    // Don't show the section at all when we don't have NASR coords —
    // OurAirports doesn't carry runway end coords reliably, so the
    // diagram would look wrong on most non-US airports.
    return null;
  }
  if (!projection || projection.runways.length === 0) {
    return null;
  }

  const { width, height, runways, magVar, hasMagVar } = projection;

  return (
    <div className="px-4 pt-3 pb-2">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] tracking-widest text-cockpit-400 uppercase">
          Airport diagram
        </span>
        {hasMagVar && (
          <button
            onClick={() => setMagNorthUp((v) => !v)}
            className="text-[10px] px-2 py-0.5 rounded border border-cockpit-700 text-cockpit-300 hover:text-cockpit-100 hover:border-cockpit-500"
            title={magNorthUp ? 'Switch to true north' : 'Switch to magnetic north (matches HSI)'}
          >
            {magNorthUp ? 'MAG ↑' : 'TRUE ↑'}
          </button>
        )}
      </div>
      <div className="bg-cockpit-900/60 border border-cockpit-700 rounded-lg overflow-hidden">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-auto"
          style={{ maxHeight: '320px' }}
          role="img"
          aria-label={`Runway diagram for ${nasr.ident}`}
        >
          {/* North arrow in the upper-right corner */}
          <NorthArrow x={width - 22} y={22} />

          {/* Runways — render closed first so they sit under the
              active ones if overlapping. */}
          {runways
            .slice()
            .sort((a, b) => (a.closed === b.closed ? 0 : a.closed ? -1 : 1))
            .map((r) => (
              <RunwayShape key={r.ident} runway={r} />
            ))}
        </svg>
      </div>
      <p className="text-[10px] text-cockpit-500 mt-1.5 leading-snug">
        Scaled depiction from FAA NASR runway end coordinates.
        {hasMagVar && magNorthUp && ` Magnetic var ${formatMagVar(magVar)}.`}
        {' '}Taxiways not rendered (future).
      </p>
    </div>
  );
}

// ─── Projection ──────────────────────────────────────────────────────

/** Project all runway ends to a unit-coordinate space, then scale to
 *  fit a viewBox bounded by PADDING_PX. North is up by default; if
 *  `magNorthUp` is true the whole drawing is rotated by `-magVar`. */
function projectRunways(
  nasr: NasrAirportDetail,
  magNorthUp: boolean,
): {
  width: number;
  height: number;
  runways: ProjectedRunway[];
  magVar: number;
  hasMagVar: boolean;
} | null {
  const center = { lat: nasr.lat, lon: nasr.lon };

  // Convert lat/lon offset to local meters via simple equirectangular
  // projection — accurate enough at airport scale (worst case ~50 NM
  // edge-to-edge, well under any rounding concern).
  const cosLat = Math.cos((center.lat * Math.PI) / 180);
  function project(lat: number, lon: number): { x: number; y: number } {
    // x = east-positive meters, y = NORTH-positive meters
    const dLatM = (lat - center.lat) * 111_320;
    const dLonM = (lon - center.lon) * 111_320 * cosLat;
    return { x: dLonM, y: dLatM };
  }

  // Magnetic rotation: if magNorthUp, rotate all points by -magVar
  // around the origin. NASR magVar is positive-east (e.g. KSIY +16°
  // means true north is 16° east of magnetic north, so to make
  // magnetic-north point up we rotate the whole scene by -magVar).
  const magVar = nasr.magVar ?? 0;
  const hasMagVar = nasr.magVar != null;
  const rotRad = magNorthUp && hasMagVar ? -(magVar * Math.PI) / 180 : 0;
  function rotate(p: { x: number; y: number }): { x: number; y: number } {
    if (rotRad === 0) return p;
    const c = Math.cos(rotRad);
    const s = Math.sin(rotRad);
    return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
  }

  // Collect every runway end pair that has lat/lon on both ends.
  const projected: Array<ProjectedRunway & { rawX1: number; rawY1: number; rawX2: number; rawY2: number; widthM: number }> = [];
  for (const rw of nasr.runways) {
    const a = rw.baseEnd;
    const b = rw.recipEnd;
    if (!a || !b) continue;
    if (a.endLat == null || a.endLon == null) continue;
    if (b.endLat == null || b.endLon == null) continue;
    const p1 = rotate(project(a.endLat, a.endLon));
    const p2 = rotate(project(b.endLat, b.endLon));
    projected.push({
      ident:    rw.ident,
      leIdent:  a.ident,
      heIdent:  b.ident,
      x1: 0, y1: 0, x2: 0, y2: 0,   // placeholder — filled below after scaling
      rawX1: p1.x, rawY1: p1.y,
      rawX2: p2.x, rawY2: p2.y,
      lengthFt: rw.lengthFt,
      widthFt:  rw.widthFt,
      widthM:   (rw.widthFt ?? 60) * 0.3048,
      closed:   false, // NASR doesn't tag closed; OurAirports does, but
                      // we don't have it on the NasrAirportDetail.
                      // Future: thread through from the parent.
    });
  }
  if (projected.length === 0) return null;

  // Find bounding box of all endpoints (in meters).
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const r of projected) {
    minX = Math.min(minX, r.rawX1, r.rawX2);
    maxX = Math.max(maxX, r.rawX1, r.rawX2);
    minY = Math.min(minY, r.rawY1, r.rawY2);
    maxY = Math.max(maxY, r.rawY1, r.rawY2);
  }
  // Pad bounds by 10% in each direction so the runway ends + labels
  // don't kiss the SVG edge.
  const bboxPadM = Math.max(60, (maxX - minX) * 0.1, (maxY - minY) * 0.1);
  minX -= bboxPadM;
  maxX += bboxPadM;
  minY -= bboxPadM;
  maxY += bboxPadM;

  // Target SVG size: aim for ~480 px wide, height computed from aspect.
  const TARGET_W = 480;
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;
  const scale = (TARGET_W - PADDING_PX * 2) / spanX;
  const height = spanY * scale + PADDING_PX * 2;
  const width = TARGET_W;

  // Apply scale + flip Y (SVG y grows downward, meters y grew
  // upward).
  for (const r of projected) {
    r.x1 = PADDING_PX + (r.rawX1 - minX) * scale;
    r.y1 = PADDING_PX + (maxY - r.rawY1) * scale;
    r.x2 = PADDING_PX + (r.rawX2 - minX) * scale;
    r.y2 = PADDING_PX + (maxY - r.rawY2) * scale;
  }

  return {
    width,
    height,
    runways: projected.map((r) => ({
      ident: r.ident,
      leIdent: r.leIdent,
      heIdent: r.heIdent,
      x1: r.x1, y1: r.y1, x2: r.x2, y2: r.y2,
      lengthFt: r.lengthFt,
      widthFt: r.widthFt,
      closed: r.closed,
    })),
    magVar,
    hasMagVar,
  };
}

// ─── Components ──────────────────────────────────────────────────────

function RunwayShape({ runway }: { runway: ProjectedRunway }) {
  const r = runway;
  // Stroke-width scales with proportional runway width, but clamped
  // so a tiny grass strip and a wide hardened runway both read.
  // 100 ft of runway width ≈ 8 px on screen, give or take.
  const strokeWpx = Math.min(
    MAX_RUNWAY_STROKE,
    Math.max(MIN_RUNWAY_STROKE, (r.widthFt / 100) * 8),
  );

  const fill = r.closed ? '#475569' : '#1f2937';
  const outline = r.closed ? '#94a3b8' : '#cbd5e1';

  // Centerline (dashed white inside the runway).
  const cx1 = r.x1;
  const cy1 = r.y1;
  const cx2 = r.x2;
  const cy2 = r.y2;
  const dx = cx2 - cx1;
  const dy = cy2 - cy1;
  const len = Math.hypot(dx, dy);
  if (len < 4) return null;  // degenerate
  const ux = dx / len;
  const uy = dy / len;
  // Threshold marks: short perpendicular bars at each end.
  const thMarkLen = strokeWpx * 0.42;
  const px = -uy;  // perpendicular unit vector
  const py = ux;

  const thresholdMark = (cx: number, cy: number) => `
    M ${cx - px * thMarkLen} ${cy - py * thMarkLen}
    L ${cx + px * thMarkLen} ${cy + py * thMarkLen}
  `;

  // Place ident labels slightly past each end along the centerline so
  // they don't sit on top of the threshold bars.
  const labelOffsetPx = strokeWpx * 0.65 + 8;
  const labelLE = {
    x: cx1 - ux * labelOffsetPx,
    y: cy1 - uy * labelOffsetPx,
  };
  const labelHE = {
    x: cx2 + ux * labelOffsetPx,
    y: cy2 + uy * labelOffsetPx,
  };

  // Rotate labels to align with the runway, but keep them readable
  // (no upside-down text — flip 180° if angle is in the bottom half).
  let angleDeg = (Math.atan2(uy, ux) * 180) / Math.PI;
  // Compute angle for the HE label as-is; LE is the opposite direction.
  const heAngle = normalizeAngle(angleDeg);
  // For the LE label, the underlying direction is -ux/-uy.
  let leAngle = normalizeAngle(angleDeg + 180);

  return (
    <g>
      {/* Runway body */}
      <line
        x1={cx1} y1={cy1}
        x2={cx2} y2={cy2}
        stroke={fill}
        strokeWidth={strokeWpx}
        strokeLinecap="butt"
      />
      {/* Outline so it pops against dark background */}
      <line
        x1={cx1} y1={cy1}
        x2={cx2} y2={cy2}
        stroke={outline}
        strokeWidth={Math.max(1, strokeWpx * 0.12)}
        strokeOpacity={0.6}
      />
      {/* Centerline dashed */}
      <line
        x1={cx1} y1={cy1}
        x2={cx2} y2={cy2}
        stroke="#fafafa"
        strokeWidth={Math.max(0.6, strokeWpx * 0.08)}
        strokeDasharray={`${strokeWpx * 0.6} ${strokeWpx * 0.6}`}
        strokeOpacity={0.65}
      />
      {/* Threshold bars at each end */}
      <path d={thresholdMark(cx1, cy1)} stroke="#fafafa" strokeWidth={Math.max(1, strokeWpx * 0.18)} />
      <path d={thresholdMark(cx2, cy2)} stroke="#fafafa" strokeWidth={Math.max(1, strokeWpx * 0.18)} />

      {/* CLOSED X — diagonal cross over the body */}
      {r.closed && (
        <>
          <line x1={cx1} y1={cy1} x2={cx2} y2={cy2} stroke="#f87171" strokeWidth={Math.max(1.5, strokeWpx * 0.25)} />
          <line x1={cx1} y1={cy2} x2={cx2} y2={cy1} stroke="#f87171" strokeWidth={Math.max(1.5, strokeWpx * 0.25)} />
        </>
      )}

      {/* Designators */}
      <RunwayLabel x={labelLE.x} y={labelLE.y} text={r.leIdent} angleDeg={leAngle} />
      <RunwayLabel x={labelHE.x} y={labelHE.y} text={r.heIdent} angleDeg={heAngle} />
    </g>
  );
}

function RunwayLabel({ x, y, text, angleDeg }: { x: number; y: number; text: string; angleDeg: number }) {
  // Keep labels right-side-up: flip 180° if the math angle would put
  // the text upside down.
  let a = angleDeg;
  if (a > 90 || a < -90) a = a + 180;
  return (
    <g transform={`translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${a.toFixed(1)})`}>
      <text
        x={0}
        y={0}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="ui-monospace, monospace"
        fontSize={12}
        fontWeight={600}
        fill="#fafafa"
        stroke="#0b0d0f"
        strokeWidth={2.5}
        paintOrder="stroke"
      >
        {text}
      </text>
    </g>
  );
}

function NorthArrow({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} aria-hidden>
      <circle cx={0} cy={0} r={11} fill="#0b0d0f" stroke="#475569" strokeWidth={0.6} />
      {/* North-pointing triangle */}
      <polygon points="0,-8 4,4 0,1 -4,4" fill="#ef4444" stroke="#fafafa" strokeWidth={0.5} />
      <text
        x={0}
        y={-13}
        textAnchor="middle"
        fontSize={8}
        fontFamily="ui-monospace, monospace"
        fontWeight={700}
        fill="#fafafa"
        stroke="#0b0d0f"
        strokeWidth={1.5}
        paintOrder="stroke"
      >
        N
      </text>
    </g>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function normalizeAngle(deg: number): number {
  let a = deg;
  while (a <= -180) a += 360;
  while (a > 180) a -= 360;
  return a;
}

function formatMagVar(magVar: number): string {
  if (magVar === 0) return '0°';
  const dir = magVar > 0 ? 'E' : 'W';
  return `${Math.abs(magVar).toFixed(0)}°${dir}`;
}

// Future taxiway rendering placeholder. Wire this in once we have an
// OSM aeroway=taxiway proxy endpoint. The signature should look like:
//   pushTaxiways(svgGroupRef, taxiwayFeatures, project, scale)
// where project + scale come from the same projection used for runways
// so the two render in the same coordinate space.
export const __TAXIWAY_RENDERER_NOT_WIRED__ = true;

// Exported for testability — runway projection math.
export const __testables__ = { projectRunways };
